// Works out what is wrong with an uploaded sheet and what it would take to fix it.
// Reports issues; applying them lives in excelFormatFixer.js.

import {
  COLUMN_INDEX,
  COLUMN_LETTERS,
  detectHeaderRow,
  matchesSectionKeywords,
  normalizeHeader,
} from "./excelStructure";

const cellText = (row, index) => String((row && row[index]) || "").trim();

/**
 * Compare a sheet against the current Settings.
 *
 * Issues carry a `fix` of either:
 *   "settings" - correctable by updating Settings, no file change
 *   "file"     - needs the Excel file itself to be rewritten
 *   "manual"   - nothing can be done automatically
 *
 * @param {Array<Array>} rawData - sheet as an array of row arrays
 * @param {object} settings - current Excel settings
 */
export const detectFormatIssues = (rawData, settings = {}) => {
  const configured = settings.columnHeaders || {};
  const detected = detectHeaderRow(rawData, configured);
  const issues = [];

  if (!detected) {
    issues.push({
      id: "noHeaderRow",
      fix: "manual",
      title: "Could not find the column header row",
      detail:
        "None of the first 10 rows read like column headers. Check that the sheet has a row naming the columns, such as Item No., Unit No. and Lecturer.",
    });
    return { detected: null, issues, canFix: false };
  }

  const headerRow = detected.rowNumber;
  const startRow = headerRow + 1; // data always begins on the row after the headers

  if (Number(settings.headerRow) !== headerRow) {
    issues.push({
      id: "headerRow",
      fix: "settings",
      title: `Column Header Row should be ${headerRow}`,
      detail: `Settings says row ${settings.headerRow}, but the column names are on row ${headerRow}.`,
      value: headerRow,
    });
  }

  if (Number(settings.startRow) !== startRow) {
    issues.push({
      id: "startRow",
      fix: "settings",
      title: `Starting Row should be ${startRow}`,
      detail: `Settings says row ${settings.startRow}. Data always begins on the row after the headers, so row ${startRow}.`,
      value: startRow,
    });
  }

  const differing = COLUMN_LETTERS.filter(
    (letter) => normalizeHeader(detected.names[letter]) !== normalizeHeader(configured[letter])
  );

  // A cell with different wording is a Settings fix; a blank one has to be written
  // into the sheet, because there is no name there to adopt.
  const renamed = differing.filter((letter) => detected.names[letter]);
  const missing = differing.filter((letter) => !detected.names[letter] && configured[letter]);

  if (missing.length > 0) {
    issues.push({
      id: "emptyHeaders",
      fix: "file",
      title: `${missing.length} column${
        missing.length === 1 ? " has" : "s have"
      } no header text in the sheet`,
      detail: missing
        .map((letter) => `Column ${letter} is blank on row ${headerRow}; Settings calls it "${configured[letter]}"`)
        .join("\n"),
      value: missing.map((letter) => ({ letter, name: configured[letter] })),
      columns: missing,
    });
  }

  if (renamed.length > 0) {
    issues.push({
      id: "headerNames",
      fix: "settings",
      title: `${renamed.length} column name${renamed.length === 1 ? "" : "s"} differ from Settings`,
      detail: renamed
        .map(
          (letter) =>
            `${letter}: sheet has "${detected.names[letter]}", Settings expects "${
              configured[letter] || ""
            }"`
        )
        .join("\n"),
      value: renamed.reduce((names, letter) => {
        names[letter] = detected.names[letter];
        return names;
      }, {}),
      columns: renamed,
    });
  }

  // Title rows the app would read as data because B or C is not empty
  const blockedTitles = [];
  for (let i = startRow - 1; i < rawData.length; i++) {
    const row = rawData[i];
    if (!row || row.length === 0) continue;

    const title = cellText(row, COLUMN_INDEX.A);
    if (!title || !matchesSectionKeywords(title)) continue;

    const inB = cellText(row, COLUMN_INDEX.B);
    const inC = cellText(row, COLUMN_INDEX.C);
    if (inB || inC) {
      blockedTitles.push({ rowNumber: i + 1, title, inB, inC });
    }
  }

  if (blockedTitles.length > 0) {
    issues.push({
      id: "blockedTitles",
      fix: "file",
      title: `${blockedTitles.length} section title row${
        blockedTitles.length === 1 ? " has" : "s have"
      } content in B or C`,
      detail: blockedTitles
        .map(
          (entry) =>
            `Row ${entry.rowNumber} "${entry.title}" - ${
              [entry.inB && `B has "${entry.inB}"`, entry.inC && `C has "${entry.inC}"`]
                .filter(Boolean)
                .join(", ")
            }`
        )
        .join("\n"),
      value: blockedTitles,
    });
  }

  return {
    detected: { headerRow, startRow, headerNames: detected.names, score: detected.score },
    issues,
    canFix: issues.some((issue) => issue.fix !== "manual"),
  };
};

/** Split issues by how they get fixed, for the UI. */
export const groupIssues = (issues = []) => ({
  settings: issues.filter((issue) => issue.fix === "settings"),
  file: issues.filter((issue) => issue.fix === "file"),
  manual: issues.filter((issue) => issue.fix === "manual"),
});
